import { Wallet, AlertTriangle, Clock, Shield, TrendingUp, Building2 } from "lucide-react";

export interface DashboardState {
  phase: string;
  cash: number;
  loan: number;
  equityInvested: number;
  contingencyRemaining: number;
  interestAccrued: number;
  monthsPlanned: number;
  monthsDelayed: number;
  risk: number;
  confidence: number;
  estimatedValue: number;
  estimatedProfit: number;
}

function Stat({ icon, label, value, tone }: {
  icon: React.ReactNode; label: string; value: string;
  tone?: "good" | "warn" | "bad" | "default";
}) {
  const toneClass =
    tone === "good" ? "text-emerald-400"
    : tone === "warn" ? "text-amber-400"
    : tone === "bad" ? "text-destructive"
    : "text-foreground";
  return (
    <div className="rounded-lg border border-border/60 bg-card/70 p-2">
      <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
        {icon}
        <span className="truncate">{label}</span>
      </div>
      <p className={`font-mono text-[12px] font-bold mt-0.5 ${toneClass}`}>{value}</p>
    </div>
  );
}

const money = (n: number) =>
  (n < 0 ? "-$" : "$") + Math.abs(Math.round(n)).toLocaleString();

export default function LiveDashboard({ s }: { s: DashboardState }) {
  const cashTone = s.cash < 0 ? "bad" : s.cash < 25_000 ? "warn" : "good";
  const riskTone = s.risk >= 90 ? "bad" : s.risk >= 60 ? "warn" : "good";
  const profitTone = s.estimatedProfit < 0 ? "bad" : s.estimatedProfit < 25_000 ? "warn" : "good";

  return (
    <div className="rounded-2xl border border-primary/30 bg-gradient-to-br from-card/90 to-card/60 p-3 backdrop-blur-md">
      <div className="flex items-center justify-between mb-2">
        <p className="font-heading text-[11px] font-bold text-gradient-blue tracking-wide uppercase">
          Project Command
        </p>
        <span className="text-[10px] font-mono text-muted-foreground">
          Phase · {s.phase}
        </span>
      </div>
      {s.cash < 0 && (
        <div className="mb-2 rounded-lg border border-destructive/50 bg-destructive/10 px-2 py-1.5 flex items-center gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 text-destructive shrink-0" />
          <p className="text-[10px] font-mono text-destructive">CASH CRUNCH — recovery required</p>
        </div>
      )}
      <div className="grid grid-cols-3 gap-1.5">
        <Stat icon={<Wallet className="w-3 h-3" />} label="Cash" value={money(s.cash)} tone={cashTone} />
        <Stat icon={<Building2 className="w-3 h-3" />} label="Loan" value={money(s.loan)} />
        <Stat icon={<Wallet className="w-3 h-3" />} label="Equity in" value={money(s.equityInvested)} />
        <Stat icon={<Shield className="w-3 h-3" />} label="Contingency" value={money(s.contingencyRemaining)}
          tone={s.contingencyRemaining <= 0 ? "warn" : "default"} />
        <Stat icon={<Clock className="w-3 h-3" />} label="Timeline" value={`${s.monthsPlanned + s.monthsDelayed} mo`} />
        <Stat icon={<Clock className="w-3 h-3" />} label="Delays" value={`${s.monthsDelayed} mo`}
          tone={s.monthsDelayed >= 3 ? "warn" : "default"} />
        <Stat icon={<TrendingUp className="w-3 h-3" />} label="Interest" value={money(s.interestAccrued)} />
        <Stat icon={<AlertTriangle className="w-3 h-3" />} label="Risk" value={`${s.risk}`} tone={riskTone} />
        <Stat icon={<Shield className="w-3 h-3" />} label="Confidence" value={`${s.confidence}`}
          tone={s.confidence >= 60 ? "good" : s.confidence < 30 ? "warn" : "default"} />
        <Stat icon={<TrendingUp className="w-3 h-3" />} label="Est. value" value={money(s.estimatedValue)} />
        <Stat icon={<TrendingUp className="w-3 h-3" />} label="Est. P/L" value={money(s.estimatedProfit)} tone={profitTone} />
      </div>
    </div>
  );
}